import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './import-protocols-list.html';
import { deleteImportProtocol, getImportProtocol, listImportProtocols } from '../services/api.service';
import type { ImportProtocol, ImportProtocolDetail } from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

@customElement({ name: 'import-protocols-list', template })
export class ImportProtocolsList {
  private readonly i18n: I18N = resolve(I18N);

  public protocols: ImportProtocol[] = [];
  public errorMessage = '';
  public detailErrorMessage = '';
  public loadingDetail = false;
  public selectedProtocol: ImportProtocolDetail | null = null;

  public async loading(): Promise<void> {
    await this.fetchProtocols();
  }

  public async fetchProtocols(): Promise<void> {
    this.errorMessage = '';
    try {
      this.protocols = await listImportProtocols();
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error ? error.message : String(error);
    }
  }

  public async openDetails(protocol: ImportProtocol): Promise<void> {
    this.detailErrorMessage = '';
    this.loadingDetail = true;
    try {
      this.selectedProtocol = await getImportProtocol(protocol.number);
    } catch (error: unknown) {
      this.detailErrorMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.loadingDetail = false;
    }
  }

  public closeDetails(): void {
    this.selectedProtocol = null;
  }

  public viewFiles(number: number): void {
    window.location.assign(`/gallery?protocolNumber=${encodeURIComponent(String(number))}`);
  }

  public sourceLabel(source: string): string {
    const normalized = source.toLowerCase();
    return normalized === 'web' || normalized === 'mobile'
      ? this.i18n.tr(`importProtocols.sources.${normalized}`)
      : source;
  }

  public async removeProtocol(protocol: ImportProtocol): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('importProtocols.delete.confirm', { number: protocol.number }),
      this.i18n.tr('importProtocols.delete.confirmTitle'),
    );
    if (!confirmed) return;

    try {
      await deleteImportProtocol(protocol.number);
      this.protocols = this.protocols.filter(item => item.number !== protocol.number);
      if (this.selectedProtocol?.number === protocol.number) {
        this.selectedProtocol = null;
      }
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error ? error.message : String(error);
    }
  }
}
